import { useState } from 'react';
import {
  IonButton,
  IonIcon,
  IonLabel,
  IonSpinner,
  IonText,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import {
  addOutline,
  briefcaseOutline,
  closeOutline,
  createOutline,
  trashOutline,
} from 'ionicons/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { DELETE_CAREER_ENTRY } from '../../../../graphql/mutations/careerHistory';
import { GET_MY_CAREER_ENTRIES } from '../../../../graphql/queries/careerHistory';
import type {
  DeleteCareerEntryMutation,
  DeleteCareerEntryMutationVariables,
  GetMyCareerEntriesQuery,
} from '../../../../types/graphql-generated';
import { type CareerEntry, CareerEntryModal } from './CareerEntryModal';
import { CareerEntryItem } from './CareerEntryItem';

export const CareerHistorySection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CareerEntry | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();
  const { data, loading, error } = useQuery<GetMyCareerEntriesQuery>(GET_MY_CAREER_ENTRIES);
  const entries = data?.myCareerEntries ?? [];

  const [deleteCareerEntry] = useMutation<
    DeleteCareerEntryMutation,
    DeleteCareerEntryMutationVariables
  >(DELETE_CAREER_ENTRY, {
    refetchQueries: [{ query: GET_MY_CAREER_ENTRIES }],
    awaitRefetchQueries: true,
  });

  const openNewEntry = () => {
    setEditingEntry(null);
    setIsModalOpen(true);
  };

  const openEditEntry = (entry: CareerEntry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
  };

  const showToast = (message: string, success: boolean) => {
    presentToast({
      message,
      duration: success ? 2500 : 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      ...(success ? { cssClass: 'toast-success' } : { color: 'danger' }),
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const deleteEntry = async (entry: CareerEntry) => {
    setDeletingEntryId(entry.id);
    try {
      const { data: result } = await deleteCareerEntry({
        variables: { input: { id: entry.id } },
      });
      const payload = result?.deleteCareerEntry;
      if (payload?.success) {
        showToast('Experiencia eliminada del historial', true);
      } else {
        showToast(payload?.errors[0] ?? 'No se ha podido eliminar la experiencia', false);
      }
    } catch (deleteError) {
      console.error('Error deleting career entry:', deleteError);
      showToast('No se ha podido eliminar la experiencia. Inténtalo de nuevo más tarde.', false);
    } finally {
      setDeletingEntryId(null);
    }
  };

  const confirmDeleteEntry = (entry: CareerEntry) => {
    presentAlert({
      header: 'Eliminar experiencia',
      message: `¿Seguro que quieres eliminar tu experiencia en ${entry.careerOrganization.name}?`,
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Eliminar', role: 'destructive', handler: () => void deleteEntry(entry) },
      ],
    });
  };

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <IonLabel className="block text-sm font-semibold uppercase tracking-wide text-gray-500">
          Historial
        </IonLabel>
        <IonButton fill="clear" size="small" onClick={openNewEntry}>
          <IonIcon slot="start" icon={addOutline} />
          Añadir
        </IonButton>
      </div>

      {loading && (
        <div className="flex justify-center rounded-2xl border border-[#e8ebf1] bg-white py-6">
          <IonSpinner name="crescent" />
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-white p-4">
          <IonText color="danger">
            <p className="m-0 text-sm">No se ha podido cargar el historial</p>
          </IonText>
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#cfd5df] bg-white p-5 text-center">
          <IonIcon icon={briefcaseOutline} className="mb-2 text-2xl text-slate-400" />
          <p className="m-0 text-sm font-semibold text-gray-700">Añade tu trayectoria en el fútbol</p>
          <p className="m-0 mt-1 text-xs text-slate-500">
            Incluye clubes, federaciones y otras entidades en las que hayas participado.
          </p>
        </div>
      )}

      {entries.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-[#e8ebf1] bg-white">
          {entries.map((entry) => (
            <CareerEntryItem
              key={entry.id}
              entry={entry}
              actions={
                <>
                  <IonButton
                    fill="clear"
                    size="small"
                    color="medium"
                    aria-label="Editar experiencia"
                    onClick={() => openEditEntry(entry)}
                  >
                    <IonIcon slot="icon-only" icon={createOutline} />
                  </IonButton>
                  <IonButton
                    fill="clear"
                    size="small"
                    color="danger"
                    aria-label="Eliminar experiencia"
                    disabled={deletingEntryId === entry.id}
                    onClick={() => confirmDeleteEntry(entry)}
                  >
                    {deletingEntryId === entry.id ? (
                      <IonSpinner name="crescent" />
                    ) : (
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    )}
                  </IonButton>
                </>
              }
            />
          ))}
        </div>
      )}

      <CareerEntryModal
        key={editingEntry?.id ?? 'new'}
        isOpen={isModalOpen}
        entry={editingEntry}
        onClose={closeModal}
      />
    </div>
  );
};
